import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DocumentTemplate } from "@wms/domain";
import { Alert, App, Button, Form, Input, Modal, Select, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { describeError } from "../api/client";
import { formatDate } from "../api/format";
import { ACCOUNTING_REVIEW_LABELS, PURPOSE_LABELS, VERSION_STATUS_COLORS, VERSION_STATUS_LABELS, type TemplateFilters, templatesApi } from "../api/renditions";
import { useAuthStore } from "../auth/store";
import { LoadProblem } from "../components/LoadProblem";
import { PageHeader } from "../components/PageHeader";
import { RefSelect } from "../components/RefSelect";

/** The accountant's mark of the catalogue (D1): an accounting layout in work, not yet checked — never on the document. */
export function AccountingTag({ value }: { value: string | null | undefined }) {
  if (!value || value === "not_required") return <Typography.Text type="secondary">—</Typography.Text>;
  return (
    <Tag color={value === "confirmed" ? "green" : "processing"} bordered={false} data-testid={`accounting-${value}`}>
      {ACCOUNTING_REVIEW_LABELS[value] ?? value}
    </Tag>
  );
}

export function StatusTag({ status, version }: { status: string | null | undefined; version?: number | null }) {
  if (!status) return <Typography.Text type="secondary">нет версий</Typography.Text>;
  return (
    <Tag color={VERSION_STATUS_COLORS[status] ?? "default"} bordered={false}>
      {VERSION_STATUS_LABELS[status] ?? status}
      {version ? ` · v${version}` : ""}
    </Tag>
  );
}

/**
 * KZ-DOC UI01 / TMP01–TMP03: «Администрирование → Шаблоны документов». The layouts of every kind of document with
 * their scope (common or of our organization), purpose, current version and the accountant's check. A draft is never
 * used as a published form; «Шаблоны этикеток» stays where it was.
 */
export function DocumentTemplatesPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const canWrite = can("document_templates.write");
  const [filters, setFilters] = useState<TemplateFilters>({});
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();
  const sources = useQuery({ queryKey: ["document-sources"], queryFn: templatesApi.sources, staleTime: 300_000 });
  const list = useQuery({ queryKey: ["document-templates", filters], queryFn: () => templatesApi.list(filters) });
  const create = useMutation({
    mutationFn: templatesApi.create,
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
      message.success(`Шаблон «${row.name}» создан: черновик версии 1`);
      setCreating(false);
      navigate(`/document-templates/${row.id}`);
    },
    onError: (e) => message.error(describeError(e)),
  });

  if (list.isError) return <LoadProblem error={list.error} onRetry={() => list.refetch()} />;
  const purposes = sources.data?.purposes ?? Object.entries(PURPOSE_LABELS).map(([value, label]) => ({ value, label }));
  const statuses = sources.data?.statuses ?? Object.entries(VERSION_STATUS_LABELS).map(([value, label]) => ({ value, label }));
  const kinds = (sources.data?.sources ?? []).map((s) => ({ value: s.kind, label: s.label }));
  const set = (patch: TemplateFilters) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <div data-testid="document-templates-page">
      <PageHeader
        title="Шаблоны документов"
        subtitle="Макеты для просмотра, PDF и печати документов. Изменение опубликованного макета — новая версия; учётный макет сверяет бухгалтер."
        extra={
          canWrite ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)} data-testid="templates-create">
              Создать шаблон
            </Button>
          ) : (
            <Typography.Text type="secondary">Создание — у сотрудника с правом «Шаблоны документов: черновики версий»</Typography.Text>
          )
        }
      />
      <Space wrap style={{ marginBottom: 12 }} data-testid="templates-filters">
        <Select allowClear placeholder="Вид документа: все" style={{ minWidth: 220 }} options={kinds} loading={sources.isLoading} value={filters.doc_kind} onChange={(doc_kind) => set({ doc_kind })} data-testid="templates-filter-kind" />
        <div style={{ minWidth: 220 }}>
          <RefSelect entity="companies" placeholder="Наша организация: все" value={filters.company_id} onChange={(v) => set({ company_id: (v as string | undefined) ?? undefined })} />
        </div>
        <Select allowClear placeholder="Назначение: все" style={{ minWidth: 180 }} options={purposes} value={filters.purpose} onChange={(purpose) => set({ purpose })} data-testid="templates-filter-purpose" />
        <Select allowClear placeholder="Статус: все" style={{ minWidth: 160 }} options={statuses} value={filters.status} onChange={(status) => set({ status })} data-testid="templates-filter-status" />
        <Input.Search allowClear placeholder="Название" style={{ width: 220 }} onSearch={(q) => set({ q: q || undefined })} data-testid="templates-filter-q" />
      </Space>
      <Table<DocumentTemplate>
        rowKey="id"
        size="middle"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        pagination={{ pageSize: 50, hideOnSinglePage: true, showTotal: (t) => `шаблонов: ${t}` }}
        scroll={{ x: 900 }}
        onRow={(row) => ({ onClick: () => navigate(`/document-templates/${row.id}`), style: { cursor: "pointer" } })}
        locale={{ emptyText: "Шаблонов по этим условиям нет" }}
        data-testid="templates-table"
        columns={[
          {
            title: "Название",
            dataIndex: "name",
            render: (v: string, r) => (
              <Space size={4} wrap>
                <Link to={`/document-templates/${r.id}`} onClick={(e) => e.stopPropagation()} data-testid={`template-link-${r.code}`}>
                  {v}
                </Link>
                {!r.is_active && <Tag bordered={false}>отключён</Tag>}
              </Space>
            ),
          },
          { title: "Документ", dataIndex: "doc_kind_label" },
          { title: "Наша организация", dataIndex: "company_name", render: (v: string | null) => v ?? <Typography.Text type="secondary">общий</Typography.Text> },
          { title: "Назначение", dataIndex: "purpose", render: (v: string) => PURPOSE_LABELS[v] ?? v },
          {
            title: "Версия",
            key: "version",
            render: (_: unknown, r) => (r.active_version ? `${r.active_version.version}` : r.latest_version ? `${r.latest_version}` : "—"),
            width: 90,
          },
          {
            title: "Статус",
            key: "status",
            render: (_: unknown, r) => (
              <Space size={4} wrap>
                {r.active_version && <StatusTag status="published" version={r.active_version.version} />}
                {r.latest_status && r.latest_status !== "published" && <StatusTag status={r.latest_status} version={r.latest_version} />}
                {!r.active_version && !r.latest_status && <StatusTag status={null} />}
              </Space>
            ),
          },
          { title: "Сверка бухгалтером", key: "review", render: (_: unknown, r) => <AccountingTag value={r.active_version?.accounting_review ?? null} /> },
          { title: "Действует с", key: "from", render: (_: unknown, r) => (r.active_version ? formatDate(r.active_version.valid_from) || "без ограничения" : "—"), width: 130 },
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
        Учётный макет работает сразу, отметка «Не сверено бухгалтером» стоит только в каталоге — на документе её нет. Бухгалтер подтверждает
        применимость и редакцию кнопкой «Сверено бухгалтером» в карточке шаблона.
      </Typography.Paragraph>
      {sources.isError && <Alert type="warning" showIcon message="Список видов документов не загрузился" description={describeError(sources.error)} />}

      <Modal
        title="Новый шаблон документа"
        open={creating}
        onCancel={() => setCreating(false)}
        okText="Создать черновик"
        cancelText="Отмена"
        confirmLoading={create.isPending}
        destroyOnHidden
        onOk={async () => {
          const values = await form.validateFields();
          create.mutate({ ...values, company_id: values.company_id ?? null });
        }}
      >
        <Form form={form} layout="vertical" preserve={false} initialValues={{ purpose: "operational" }}>
          <Form.Item name="name" label="Название" rules={[{ required: true, min: 3, message: "Не короче 3 символов" }]}>
            <Input maxLength={200} placeholder="Например: Накладная на отпуск запасов" data-testid="template-new-name" />
          </Form.Item>
          <Form.Item name="doc_kind" label="Вид документа" rules={[{ required: true, message: "Выберите вид документа" }]}>
            <Select options={kinds} showSearch optionFilterProp="label" data-testid="template-new-kind" />
          </Form.Item>
          <Form.Item name="purpose" label="Назначение">
            <Select options={purposes} />
          </Form.Item>
          <Form.Item name="company_id" label="Наша организация" extra="Пусто — общий макет для всех наших организаций">
            <RefSelect entity="companies" />
          </Form.Item>
          <Form.Item name="basis" label="Основание формы" extra="Например: по образцу формы приказа МФ РК №562; применимость подтверждает бухгалтер">
            <Input.TextArea maxLength={500} autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
